import type { StoreConfig, StoreOffer } from '../shared/api-types.js';
import type { PlayerData } from '../collection/PlayerState.js';
import { buyStoreOffer, claimDailyGems } from '../collection/PlayerState.js';
import { spawnGemGainFlyaway } from './Flyaway.js';

/** Full-screen store overlay. Reachable from the main menu and from
 *  the custom-pack selection screen — the shared overlay form means
 *  the player can buy currency without losing the context they were
 *  in.
 *
 *  Offers are either USD-paid or gem-paid:
 *  - USD-paid (alpha mode): clicking Buy pops a confirm modal with
 *    the alpha disclaimer ("we aren't collecting any money…"). Only
 *    the modal's Get Gems button issues the request. The double-tap
 *    is deliberate — playtesters need a beat to read the price-as-
 *    intent prompt.
 *  - Gem-paid: one-tap purchase. Matches the pack-buy convention for
 *    in-game currency. Disabled when the player can't afford it.
 *
 *  Subscription tile replaces itself with a Get Gems claim button
 *  whenever the player has an active daily-gems ticket, mirroring the
 *  main-menu daily-claim button. That means a player who's deep in
 *  the custom-pack flow doesn't have to back out to the main menu to
 *  pick up today's gems before paying for a pack.
 *
 *  `onChange` fires after any state-mutating action (purchase or
 *  claim) so the caller can re-render anything underneath that shows
 *  the gem/coin balance (the main menu's currency line, the custom-
 *  pack view's entry-cost gate, etc.). The shop's own panel re-
 *  renders automatically via `repaint()`. */
export function showStore(
  config: StoreConfig,
  playerData: PlayerData,
  onChange: () => void,
  onClose?: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'gem-shop-overlay';
  document.body.appendChild(overlay);

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
    onClose?.();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  function repaint(): void {
    overlay.innerHTML = `
      <div class="gem-shop-panel">
        <button class="gem-shop-close" type="button" aria-label="Close">×</button>
        <h2 class="gem-shop-title">Store</h2>
        <p class="gem-shop-balance">You have <strong class="gem-shop-coins">${playerData.coins.toLocaleString()} coins</strong> &middot; <strong class="gem-shop-gems">${playerData.gems.toLocaleString()} gems</strong></p>
        <div class="gem-shop-grid">
          ${config.offers.map(o => renderTile(o, playerData)).join('')}
        </div>
      </div>
    `;
    overlay.querySelector('.gem-shop-close')!.addEventListener('click', close);

    for (const offer of config.offers) {
      const tile = overlay.querySelector(`.gem-offer-tile[data-offer-id="${offer.id}"]`) as HTMLElement | null;
      if (!tile) continue;

      const subTicketId = offer.subscription?.ticketId;
      const ticketState = subTicketId ? playerData.tickets[subTicketId] : undefined;
      const subscriptionActive = !!(ticketState && ticketState.count > 0);

      if (offer.subscription && subscriptionActive) {
        // Active subscription → tile is a claim button (or a "come
        // back tomorrow" disabled state).
        const canClaim = canClaimDailyToday(ticketState!.lastUsed);
        const claimBtn = tile.querySelector('.gem-offer-claim-btn') as HTMLButtonElement | null;
        if (claimBtn) {
          claimBtn.addEventListener('click', () => {
            if (!canClaim) return;
            // Capture the button rect before repaint() — repaint replaces
            // the tile DOM, so the click target won't exist by the time
            // the promise resolves. spawnGemGainFlyaway appends to <body>
            // and floats independently of the shop re-render.
            claimDailyGems(playerData)
              .then(({ gemsGranted }) => {
                spawnGemGainFlyaway(claimBtn, gemsGranted);
                repaint();
                onChange();
              })
              .catch(err => console.error('Failed to claim daily gems:', err));
          });
        }
      } else {
        const buyBtn = tile.querySelector('.gem-offer-buy-btn') as HTMLButtonElement | null;
        if (buyBtn) {
          buyBtn.addEventListener('click', () => {
            if (typeof offer.priceUsd === 'number') {
              // USD-paid: alpha disclaimer modal before posting.
              showUsdConfirm(overlay, offer, () => doBuy(offer));
            } else {
              // Gem-paid: one-tap. The button is already disabled if
              // the player can't afford it; the server will also
              // 400 if a stale UI somehow gets through.
              doBuy(offer);
            }
          });
        }
      }
    }

    function doBuy(offer: StoreOffer): void {
      buyStoreOffer(playerData, offer.id)
        .then(() => { repaint(); onChange(); })
        .catch(err => console.error('Failed to buy store offer:', err));
    }
  }

  repaint();
}

function renderTile(offer: StoreOffer, playerData: PlayerData): string {
  const subTicketId = offer.subscription?.ticketId;
  const ticketState = subTicketId ? playerData.tickets[subTicketId] : undefined;
  const subscriptionActive = !!(ticketState && ticketState.count > 0);

  if (offer.subscription && subscriptionActive) {
    const canClaim = canClaimDailyToday(ticketState!.lastUsed);
    const remaining = ticketState!.count;
    const dayWord = remaining === 1 ? 'day' : 'days';
    return `
      <div class="gem-offer-tile gem-offer-subscription gem-offer-active" data-offer-id="${offer.id}">
        <div class="gem-offer-name">${escapeHtml(offer.name)}</div>
        <div class="gem-offer-detail">${remaining} ${dayWord} left</div>
        <button class="menu-btn primary-blue gem-offer-claim-btn" ${canClaim ? '' : 'disabled'}>
          ${canClaim ? `Get ${offer.subscription.dailyGems} Gems` : 'Claimed today'}
        </button>
        ${canClaim ? '' : '<div class="gem-offer-footnote">Come back tomorrow</div>'}
      </div>
    `;
  }

  const detail = describeGrants(offer);
  const tileClass = offer.subscription ? 'gem-offer-tile gem-offer-subscription' : 'gem-offer-tile';
  const buttonLabel = priceLabel(offer);
  // Gem-paid offers are only purchasable when the player can afford
  // them. USD-paid offers stay enabled (the confirm modal does the
  // gating, and the alpha-mode server grants unconditionally).
  const disabled = typeof offer.priceGems === 'number' && playerData.gems < offer.priceGems
    ? 'disabled'
    : '';
  // Button color signals the currency being spent: green for real
  // money (more appealing than red for opt-in spend), blue for gems
  // (matches the spirit-blue we use for gem-related UI everywhere
  // else). The red `.primary` variant is reserved for top-level
  // navigation actions like Play.
  const colorClass = typeof offer.priceGems === 'number' ? 'primary-blue' : 'primary-green';

  return `
    <div class="${tileClass}" data-offer-id="${offer.id}">
      <div class="gem-offer-name">${escapeHtml(offer.name)}</div>
      <div class="gem-offer-detail">${escapeHtml(detail)}</div>
      <button class="menu-btn ${colorClass} gem-offer-buy-btn" ${disabled}>${escapeHtml(buttonLabel)}</button>
    </div>
  `;
}

/** Player-facing summary of what the offer credits. Subscriptions
 *  read as "100 gems now + 300/day for 30 days"; one-shot offers
 *  pick the single grant they have set. */
function describeGrants(offer: StoreOffer): string {
  if (offer.subscription) {
    const upfront = offer.gems ?? 0;
    return `${upfront} gems now + ${offer.subscription.dailyGems}/day for ${offer.subscription.charges} days`;
  }
  if (offer.coins && offer.coins > 0) return `${offer.coins.toLocaleString()} coins`;
  if (offer.gems && offer.gems > 0) return `${offer.gems.toLocaleString()} gems`;
  return '';
}

function priceLabel(offer: StoreOffer): string {
  if (typeof offer.priceGems === 'number') return `${offer.priceGems} gems`;
  if (typeof offer.priceUsd === 'number') return formatUsd(offer.priceUsd);
  return '';
}

/** True iff the daily-gems ticket can be claimed today. NULL last-used
 *  means it's never been claimed (just-purchased subscription is the
 *  common case); otherwise compare UTC calendar dates against now. */
function canClaimDailyToday(lastUsed: string | null): boolean {
  if (!lastUsed) return true;
  const last = new Date(lastUsed).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  return last < today;
}

function showUsdConfirm(parent: HTMLElement, offer: StoreOffer, onConfirm: () => void): void {
  const modal = document.createElement('div');
  modal.className = 'gem-confirm-overlay';
  modal.innerHTML = `
    <div class="gem-confirm-panel">
      <p class="gem-confirm-message">
        While Cardverse is in alpha, we aren't collecting any money.
        Please click "Get Gems" only if you would realistically spend
        <strong>${formatUsd(offer.priceUsd ?? 0)}</strong> for this purchase.
      </p>
      <div class="gem-confirm-buttons">
        <button class="action-button secondary gem-confirm-cancel" type="button">Cancel</button>
        <button class="action-button gem-confirm-ok" type="button">Get Gems</button>
      </div>
    </div>
  `;
  parent.appendChild(modal);

  const dismiss = (): void => {
    modal.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    // Esc cancels — same convention as the StageIntroView modal.
    if (e.key === 'Escape') {
      e.stopPropagation();
      dismiss();
    }
  };
  document.addEventListener('keydown', onKey);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) dismiss();
  });
  modal.querySelector('.gem-confirm-cancel')!.addEventListener('click', dismiss);
  modal.querySelector('.gem-confirm-ok')!.addEventListener('click', () => {
    dismiss();
    onConfirm();
  });
  setTimeout(() => (modal.querySelector('.gem-confirm-ok') as HTMLButtonElement).focus(), 0);
}

function formatUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
